import { useState } from "react";
import { toast } from "sonner";
import { X, HeartHandshake } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { openBillingPortal } from "@/lib/checkout";
import { useAuth } from "@/context/AuthContext";

export function CancelSaveModal({ open, onClose }) {
  const { user, refreshUser } = useAuth();
  const [busy, setBusy] = useState(false);
  if (!open) return null;

  const used = user?.retention_offer_used;

  const accept = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/retention-offer");
      toast.success(data.detail || "Offer applied!");
      await refreshUser();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] grid place-items-center bg-black/60 p-4" data-testid="cancel-save-modal">
      <div className="w-full max-w-md bg-[#0d1730] border border-white/10 rounded-3xl p-8 text-center text-slate-100">
        <button data-testid="cancel-save-close" onClick={onClose} className="absolute top-4 right-4 text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
        <div className="w-14 h-14 rounded-2xl bg-emerald-500/15 grid place-items-center mx-auto mb-4"><HeartHandshake className="w-7 h-7 text-emerald-400" /></div>
        <h2 className="text-2xl font-bold">Before you go…</h2>
        {used ? (
          <p className="text-slate-400 mt-2 text-sm">You've already used your one-time offer. You can still cancel anytime from the billing portal.</p>
        ) : (
          <p className="text-slate-400 mt-2 text-sm">Stay with us and take <strong className="text-white">50% off your next month</strong> — applied instantly, no code needed.</p>
        )}
        <div className="mt-6 space-y-3">
          {!used && (
            <button data-testid="cancel-save-accept" onClick={accept} disabled={busy} className="w-full h-12 rounded-xl bg-emerald-500 text-[#04140c] font-semibold hover:bg-emerald-400 transition-colors disabled:opacity-50">
              {busy ? "Applying…" : "Keep my plan · 50% off next month"}
            </button>
          )}
          <button data-testid="cancel-save-proceed" onClick={() => openBillingPortal().catch(() => {})} className="w-full h-11 rounded-xl border border-white/15 text-slate-300 text-sm hover:bg-white/5 transition-colors">
            Cancel anyway
          </button>
        </div>
      </div>
    </div>
  );
}
